import { useQueryClient } from '@tanstack/react-query'
import ReportCycleUpsellSvg from 'ext-ui/svg/illustration/ReportCycleUpsellSvg'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import ReportTransactionsBoxAndLinks from 'src/components/ReportTransactionsBoxAndLinks/ReportTransactionsBoxAndLinks'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import StatementSummaryHeader from 'src/components/StatementSummaryHeader/StatementSummaryHeader'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { IssuerCodes, TransactionFilterOptions } from 'src/enums'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import {
  useBackgroundDispatch,
  useChangedEffect,
  useEventListener,
  usePrevious,
} from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import { useQueryCreditCard } from 'src/queries/creditCard.queries'
import { useQueryOrgExpenseReport } from 'src/queries/orgExpenseReports.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { updateSelectedTransactions } from 'src/redux/actions/data/transactions.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectExpenseReportIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'

import styles from './Statement.style'

const OrgExpenseReport: React.FC = () => {
  const translate = useTranslate()
  const reportId = useSelector(selectExpenseReportIdFromRoute)
  const { data: report, error: fetchFailure } = useQueryOrgExpenseReport({
    reportId,
  })
  const reportPrevious = usePrevious(report)
  const user = useSelector(selectUserListModelFromState)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const queryClient = useQueryClient()
  const { data: creditCard } = useQueryCreditCard({
    creditCardId: report?.accountId,
    retry: true,
  })

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })
  const clientHeight = useMemo(
    () => viewportHeight.height - 122,
    [viewportHeight],
  )

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEventListener('resize', updateWindowSize)

  const dispatch = useBackgroundDispatch()

  const [initialLoad, setInitialLoad] = useState(true)

  // listen for changes to report close date to re-fetch transactions
  useChangedEffect(() => {
    if (reportPrevious && reportPrevious?.until !== report?.until) {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
    }
  }, [reportPrevious, report])

  // clear selected transactions on first render
  useEffect(() => {
    dispatch(updateSelectedTransactions())
  }, [])

  if (!user?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPage />
  }

  return (
    <SaasUpgradeOrContent
      illustration={<ReportCycleUpsellSvg />}
      title={translate('Expense Report Cycle')}
      description={translate(
        "For each billing cycle, you'll receive an automatically generated expense report matching your bank statement—complete with all virtual* and physical card transactions (if applicable).",
      )}
      disclaimer={
        <Trns
          source="*Virtual cards available for ${selectIssuersLink}"
          expressions={{
            selectIssuersLink: (
              <Lnk
                className="underline-link"
                href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                ariaLabel={translate('select issuers')}
              >
                {translate('select issuers')}
              </Lnk>
            ),
          }}
        />
      }
      ctaDetailSrc={
        'Upgrade to ${tier} today and receive automatically generated expense reports synced with your billing cycle.'
      }
      feature={UserFeature.AUTOMATED_EXPENSE_REPORTS}
    >
      {!fetchFailure ? (
        <BackgroundPage className="Statement" css={styles}>
          <BackgroundPageContent noStackingIndex>
            <StatementSummaryHeader report={report} creditCard={creditCard} />
            <ReportTransactionsBoxAndLinks
              className="Statement transactions"
              context={TransactionReportType.ExpenseReport}
              showIntegrationsLink
              showConnection
              design={TransactionsTableLargeDesigns.UserAndCardAndStatus}
              hasReceiptToolsDropdown
              initialLoad={initialLoad}
              setInitialLoad={setInitialLoad}
              canViewAllTransactions={false}
              reportId={reportId}
              visibleFilters={[
                ...// using is issuer Regions check until BE updates Regions cards to accurately set physicalClearingsEnabled
                (organization?.physicalClearingsEnabled ||
                [IssuerCodes.Regions, IssuerCodes.RegionsTest].includes(
                  creditCard?.issuer as IssuerCodes,
                )
                  ? [TransactionFilterOptions.TransactionOrigin]
                  : []),
                TransactionFilterOptions.ExpenseCategoryStatus,
                TransactionFilterOptions.ExpenseCategoryLabels,
                ...(receiptManagementEnabled !== undefined
                  ? [TransactionFilterOptions.ReceiptStatus]
                  : []),
                ...(activeIntegration !== undefined &&
                user?.hasOrgBookkeeperPermissions
                  ? [
                      TransactionFilterOptions.TransactionSyncStatus,
                      TransactionFilterOptions.ReceiptSyncStatus,
                    ]
                  : []),
                TransactionFilterOptions.CardUser,
                TransactionFilterOptions.Departments,
                TransactionFilterOptions.MerchantCategory,
                TransactionFilterOptions.ReviewStatus,
              ]}
              clientHeight={clientHeight || 0}
            />
          </BackgroundPageContent>
        </BackgroundPage>
      ) : (
        <NotFoundBackgroundPage />
      )}
    </SaasUpgradeOrContent>
  )
}

export default OrgExpenseReport
